import { Link } from "@inertiajs/react"
import type { LucideIcon } from "lucide-react"
import { ChevronRight } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

interface FeatureCardProps {
  title: string
  icon: LucideIcon
  iconColor?: string
  iconBgColor?: string
  href?: string
  children: React.ReactNode
  className?: string
  animationDelay?: number
}

export function FeatureCard({
  title,
  icon: Icon,
  iconColor = "text-primary",
  iconBgColor = "bg-primary/10",
  href,
  children,
  className,
  animationDelay = 0,
}: FeatureCardProps) {
  const content = (
    <Card
      className={cn(
        "group relative overflow-hidden transition-all duration-300",
        "hover:-translate-y-1 hover:shadow-lg",
        "animate-in fade-in slide-in-from-bottom-4 fill-mode-both",
        className,
      )}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="to-primary/[0.02] pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent opacity-0 transition-opacity group-hover:opacity-100" />

      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <div className={cn("rounded-xl p-3", iconBgColor)}>
          <Icon className={cn("size-5", iconColor)} />
        </div>
        <CardTitle className="flex-1 text-lg font-semibold">{title}</CardTitle>
        {href && (
          <ChevronRight className="text-muted-foreground size-5 transition-transform group-hover:translate-x-1" />
        )}
      </CardHeader>

      <CardContent>{children}</CardContent>
    </Card>
  )

  if (href) {
    return <Link href={href}>{content}</Link>
  }

  return content
}
